import { styled } from "goober";
import { useQuery } from "@rocicorp/zero/react";
import { type CSSProperties, useState } from "react";
import { CONTEXT_LIMIT_MESSAGE } from "../../../shared/contextLimit";
import { queries } from "../../../shared/queries";
import { SUB_AGENT_UPDATE_PREFIX } from "../../../shared/subAgent";
import { routes, useMakeUrl } from "../../lib/urls";
import { Icon, type IconName } from "../Icon";
import { Link } from "../Link";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";

const systemMessageIconByPrefix: [prefix: string, icon: IconName][] = [
  ["Saved:", "save"],
  ["Restored", "history"],
  ["Workflow renamed", "pencil"],
];

export function systemMessageIcon(text: string): IconName {
  if (text === CONTEXT_LIMIT_MESSAGE) return "brain";
  if (text.startsWith(SUB_AGENT_UPDATE_PREFIX)) return "3b-mark";
  for (const [prefix, icon] of systemMessageIconByPrefix) {
    if (text.startsWith(prefix)) return icon;
  }
  return "info";
}

const Pill = styled("div")`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
  border-radius: 8px;
  width: fit-content;
`;

export function SystemNotice({
  icon,
  text,
  style,
}: {
  icon: IconName;
  text: string;
  style?: CSSProperties;
}) {
  return (
    <Pill style={style}>
      <Icon name={icon} size={12} color="var(--text-secondary)" />
      <Text variant="text-small" color="var(--text-secondary)">
        {text}
      </Text>
    </Pill>
  );
}

const skillPillStyles = `
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px 0 6px;
  height: 20px;
  min-width: 0;
  max-width: 200px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  text-decoration: none;
`;

const SkillPill = styled(Link)`
  ${skillPillStyles}
  &:hover {
    background: var(--alpha8);
  }
`;

const StaticSkillPill = styled("span")`
  ${skillPillStyles}
`;

export function LoadedSkillNotice({ name }: { name: string }) {
  const makeUrl = useMakeUrl();
  const [skill] = useQuery(queries.skillByName({ name }));
  const content = (
    <>
      <Icon name="skills" size={10} style={{ flexShrink: 0 }} />
      <Text variant="text-small-md" ellipsis>
        {name}
      </Text>
    </>
  );
  return (
    <Pill>
      <Icon name="notebook-text" size={12} color="var(--text-secondary)" />
      <Text variant="text-small" color="var(--text-secondary)">
        Loaded
      </Text>
      {skill ? (
        <SkillPill to={makeUrl(routes.modal.skill, { id: skill.id })}>
          {content}
        </SkillPill>
      ) : (
        <StaticSkillPill>{content}</StaticSkillPill>
      )}
    </Pill>
  );
}

// Workflow agent updates carry the outcome for the model on the lines after the heading.
export function systemMessageText(text: string): string {
  if (text === CONTEXT_LIMIT_MESSAGE) return "Reached the context limit";
  if (text.startsWith(SUB_AGENT_UPDATE_PREFIX))
    return text.slice(SUB_AGENT_UPDATE_PREFIX.length).split("\n")[0]!;
  return text;
}

// Git-push system messages lead with the author's name, so we match the stable
// trailing phrase rather than a prefix.
export function isPushMessage(text: string): boolean {
  return text.split("\n")[0]?.endsWith("pushed from local") ?? false;
}

function CollapsibleSystemMessage({
  text,
  icon,
}: {
  text: string;
  icon: IconName;
}) {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split("\n");
  const summary = lines[0] ?? "";
  const details = lines.slice(1).filter(Boolean).join("\n");

  if (!details) {
    return (
      <SystemNotice icon={icon} text={summary} style={{ marginTop: 16 }} />
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        marginTop: 16,
      }}
    >
      <CollapsibleHeader onPress={() => setExpanded((prev) => !prev)}>
        <CollapsibleHeaderInner>
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon name={icon} size={12} color="var(--text-secondary)" />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={expanded ? "minus" : "plus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
          <Text variant="text-small" color="var(--text-secondary)">
            {summary}
          </Text>
        </CollapsibleHeaderInner>
      </CollapsibleHeader>
      <ExpandWrapper data-expanded={expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            <Text variant="text-small" color="var(--text-secondary)">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {details}
              </span>
            </Text>
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}

export function MergeMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="merge" />;
}

export function PushMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="laptop" />;
}

export function GitSyncMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="git-branch" />;
}
